// Krok 4 „Popatrz i wybierz” (kurs v2, SPEC §23.26): w polu obrazka stoi kotwica słowa (jego własny obrazek albo kafelek z emoji i sytuacją po polsku), obok pytanie
// (można je odsłuchać normalnie 🔊 i wolno 🐢) i cztery słowa do dotknięcia. Jedno jest dobre. Błędna odpowiedź robi się czerwona i zostaje wyłączona; po dwóch pomyłkach pokazujemy dobrą
// (nic nie rozwiązuje się samo wcześniej). Po rozstrzygnięciu ekran ODSŁANIA słowo — wielkie słowo, polskie znaczenie i dźwięk — bo ten krok wprowadza słowa:
// uczeń ma wyjść z niego ze słowem, które zobaczył i usłyszał, nawet jeśli nie trafił. Miejsce na odsłonięcie jest zarezerwowane od początku, więc nic się nie przesuwa.
// Wynik: `correct` = dobra odpowiedź za pierwszym razem (nie liczy się do statystyki słowa — sprawdza je dopiero krok 5).
import { config } from '../config.ts';
import { sayKey, type FeedbackLang } from '../lib/feedbackVoice.ts';
import { audioIds } from '../speech/audioIds.ts';
import type { QuizTask } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h, shake } from './dom.ts';
import { tx, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { anchorPicture, fitProps, fitToWindow, icon, labelFor, translateButton } from './kit.ts';
import { L } from './taskText.ts';

const T = {
  chip: { en: 'Look and choose', pl: 'Popatrz i wybierz' },
  listenWord: { en: 'Listen to the word', pl: 'Posłuchaj słowa' },
} as const satisfies Record<string, Pair>;

/** Po tylu błędnych odpowiedziach pokazujemy dobrą i idziemy dalej. */
export const QUIZ_MAX_WRONG = 2;

export function renderQuiz(task: QuizTask, env: TaskEnv): TaskView {
  const { ctx } = env;
  const { player, teacher } = ctx;
  const langNow = (): FeedbackLang => teacher?.lang() ?? config.teacher.defaultLang;
  let wrong = 0;
  let finished = false;
  const tried = new Set<number>();

  const playQuestion = (): Promise<void> => player.play(task.audioId, { text: task.q, lang: 'en' });
  const wordClip = { id: audioIds.word(task.word), fallback: { text: task.word, lang: 'en' as const } };
  const playWord = (): Promise<void> => player.play(wordClip.id, wordClip.fallback);
  const playQuestionSlow = (): Promise<void> => player.play(task.slowAudioId ?? task.audioId, { text: task.q, lang: 'en', slow: true });
  const speaker = h('button', { type: 'button', class: 'speaker-btn', 'data-testid': 'quiz-listen', onclick: () => void playQuestion() }, icon('speaker'));
  labelFor(ctx, speaker, L.listenToSentence);
  const slow = h('button', { type: 'button', class: 'speaker-btn slow', 'data-testid': 'quiz-listen-slow', onclick: () => void playQuestionSlow() }, icon('turtle')); // 🐢 obok głośnika (patch 55)
  labelFor(ctx, slow, L.slowly);

  const prompt = h('p', { class: 'prompt', 'data-testid': 'prompt' }, tx(ctx, L.inPolishLabel), h('strong', null, task.pl));
  const plToggle = translateButton(false, (on) => { prompt.hidden = !on; }, ctx);
  prompt.hidden = true;

  // Pasek na dole ma wysokość od początku: przezroczysty, wyłączony „Dalej” (bez testid) trzyma miejsce, więc po rozstrzygnięciu nic na ekranie się nie przesuwa
  const actions = h('div', { class: 'actions-bar single' }, h('button', { type: 'button', class: 'btn primary bar-spacer', disabled: true, 'aria-hidden': 'true', tabindex: '-1' }, tx(ctx, L.next), icon('arrow')));
  const options = h('div', { class: 'options', 'data-testid': 'options', role: 'group' });
  const buttons = task.options.map((text, i) => h('button', { type: 'button', class: 'option', 'data-testid': 'option', 'data-i': i, onclick: () => choose(i) }, text));
  options.append(...buttons);

  // odsłonięcie słowa: miejsce jest od początku (niewidoczne), po rozstrzygnięciu pojawia się słowo, znaczenie i przycisk dźwięku
  const listenWord = h('button', { type: 'button', class: 'speaker-btn small', 'data-testid': 'reveal-listen', onclick: () => void playWord() }, icon('speaker'));
  labelFor(ctx, listenWord, T.listenWord);
  const reveal = h('div', { class: 'reveal pending', 'data-testid': 'quiz-reveal', 'aria-live': 'polite', 'aria-hidden': 'true' },
    listenWord,
    h('p', { class: 'reveal-line' }, h('strong', { class: 'reveal-word', 'data-testid': 'reveal-word' }, task.word), h('span', { class: 'reveal-dot', 'aria-hidden': 'true' }, ' · '), h('span', { class: 'reveal-pl', 'data-testid': 'reveal-pl' }, task.wordPl)),
  );

  const finish = (correct: boolean): void => {
    finished = true;
    for (const [i, b] of buttons.entries()) {
      b.disabled = true;
      b.classList.toggle('right', i === task.correct);
      if (i !== task.correct && !tried.has(i)) b.classList.add('muted');
    }
    reveal.classList.remove('pending');
    reveal.removeAttribute('aria-hidden');
    // dobra odpowiedź: pochwała i słowo; zła: samo słowo (uczeń ma je usłyszeć w każdym przypadku)
    void player.playAll(correct ? [sayKey('good', langNow()), wordClip] : [wordClip], config.teacher.gapMs);
    actions.replaceChildren(button([tx(ctx, L.next), icon('arrow')], () => env.done({ taskId: task.id, kind: 'quiz', ref: task.word, correct, attempts: wrong + 1 }), { class: 'btn primary', testid: 'next' }));
  };

  function choose(i: number): void {
    if (finished || tried.has(i)) return;
    if (i === task.correct) return finish(wrong === 0);
    wrong++;
    tried.add(i);
    const b = buttons[i]!;
    b.classList.add('wrong');
    b.disabled = true;
    shake(b);
    if (wrong >= QUIZ_MAX_WRONG) return finish(false);
    void player.playAll([sayKey('tryAgain', langNow())], config.teacher.gapMs);
  }

  const el = h(
    'section',
    { class: 'task quiz exercise screen', 'data-task': 'quiz', 'data-testid': 'task-quiz', ...fitProps(task.q.length) },
    h('div', { class: 'tools-row' }, txText(ctx, h('p', { class: 'chip' }), T.chip), plToggle),
    h('div', { class: 'ex-main screen-main' },
      h('div', { class: 'speaker-row' }, speaker, slow),
      h('div', { class: 'split' },
        h('div', { class: 'col' },
          h('p', { class: 'sentence big', 'data-testid': 'quiz-question' }, task.q),
          prompt,
          options,
          reveal,
        ),
        h('div', { class: 'side' }, anchorPicture(task)),
      ),
    ),
    actions,
  );
  void playQuestion();
  const fit = fitToWindow(el);
  return { el, destroy: () => { fit.dispose(); player.stop(); } };
}
